import type { FilterConfig, FilterOption } from '@supply-mover-2-0/core';
import { ApiService } from '@supply-mover-2-0/core';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { FormField } from '../forms/FormField';
import { Select } from '../forms/Select';
import type { SelectSearchOption } from '../forms/SelectSearch';
import { SelectSearch } from '../forms/SelectSearch';

/**
 * Geolocation permission state.
 *
 *   - 'unknown' — haven't checked yet (initial state; also the safe
 *      fallback when the Permissions API isn't available, e.g. older
 *      Safari).
 *   - 'granted' — already approved, requesting position won't prompt.
 *   - 'prompt'  — will prompt the user the first time we request a
 *      position. UI leaves "Me" enabled — clicking it triggers the
 *      browser prompt naturally.
 *   - 'denied'  — explicitly denied. "Me" is grayed out with a hint
 *      explaining why, so the user understands why it doesn't work
 *      instead of clicking it and watching nothing happen.
 *   - 'unsupported' — `navigator.geolocation` doesn't exist (or we're
 *      SSR-rendered). Same UX as 'denied'.
 */
type GeolocationStatus =
	| 'unknown'
	| 'granted'
	| 'prompt'
	| 'denied'
	| 'unsupported';

interface ProximityCenterData {
	centerType: 'me' | 'client' | 'location';
	clientId?: number;
	locationId?: string;
	lat?: number;
	lng?: number;
	radius: number;
}

interface ProximitySearchResult {
	id: string;
	type: 'client' | 'city' | 'zip' | 'county';
	label: string;
	subtitle?: string;
	lat?: number;
	lng?: number;
	count?: number;
}

interface BaseProximityFilterProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: Array<FilterOption>;
	onValuesChange: (values: Array<FilterOption>) => void;
	disabled?: boolean;
	isModalOpen?: boolean;
	isPreset?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	layout?: 'sidebar' | 'tray' | 'inline';
}

const RADIUS_OPTIONS = [
	{ value: '5', label: '5 miles' },
	{ value: '10', label: '10 miles' },
	{ value: '15', label: '15 miles' },
	{ value: '20', label: '20 miles' },
	{ value: '25', label: '25 miles' },
	{ value: '50', label: '50 miles' },
	{ value: '100', label: '100 miles' },
];

const ME_CENTER_VALUE = 'me';
const ME_CENTER_LABEL = 'Me (Current Location)';

function buildFilterOption(
	centerType: 'me' | 'client' | 'location',
	radius: number,
	centerLabel: string,
	extraData: Partial<ProximityCenterData> = {},
): FilterOption {
	const idParts =
		centerType === 'me'
			? `me:${String(radius)}`
			: centerType === 'client'
				? `client:${String(extraData.clientId ?? '')}:${String(radius)}`
				: `location:${extraData.locationId ?? ''}:${String(radius)}`;

	return {
		id: idParts,
		label: `${centerLabel} - ${String(radius)} miles`,
		data: {
			centerType,
			radius,
			...extraData,
		} satisfies ProximityCenterData,
	};
}

function parseCenterFromFilterOption(option: FilterOption): {
	centerType: 'me' | 'client' | 'location';
	centerValue: string;
	centerLabel: string;
	radius: number;
} {
	const data = option.data as ProximityCenterData | undefined;
	if (data != null) {
		const centerLabel =
			data.centerType === 'me'
				? ME_CENTER_LABEL
				: option.label.replace(/ - \d+ miles$/, '');
		return {
			centerType: data.centerType,
			centerValue:
				data.centerType === 'me'
					? ME_CENTER_VALUE
					: data.centerType === 'client'
						? `client:${String(data.clientId ?? '')}`
						: `location:${data.locationId ?? ''}`,
			centerLabel,
			radius: data.radius,
		};
	}
	// Legacy fallback: old nearMe filter format where id is just a radius string
	const idStr = String(option.id);
	return {
		centerType: 'me',
		centerValue: ME_CENTER_VALUE,
		centerLabel: ME_CENTER_LABEL,
		radius: parseInt(idStr, 10) || 25,
	};
}

export const BaseProximityFilter: React.FC<BaseProximityFilterProps> = ({
	filterType,
	config,
	selectedValues,
	onValuesChange,
	disabled = false,
	isModalOpen = false,
	isPreset = false,
	size = 'md',
}) => {
	const { t } = useTranslation();
	const searchResultsRef = useRef<Map<string, ProximitySearchResult>>(
		new Map(),
	);
	const [isResolvingCoords, setIsResolvingCoords] = useState(false);

	// PD-13545 — silently probe geolocation permission on mount so we can
	// gray out the "Me" option when the browser will refuse to provide a
	// position. We use `navigator.permissions.query` because it does NOT
	// trigger the browser permission prompt (unlike calling
	// `getCurrentPosition`, which would prompt as soon as the filter renders
	// — invasive and surprising for the user).
	const [geolocationStatus, setGeolocationStatus] =
		useState<GeolocationStatus>('unknown');

	useEffect(() => {
		if (typeof navigator === 'undefined') {
			setGeolocationStatus('unsupported');
			return;
		}
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (navigator.geolocation == null) {
			setGeolocationStatus('unsupported');
			return;
		}
		// Permissions API isn't on Safari < 16. Without it we can't
		// distinguish denied vs prompt without prompting, so leave status
		// as 'unknown' and let the user try — same as before this UX work.
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (navigator.permissions?.query == null) {
			return;
		}

		let cancelled = false;
		navigator.permissions
			.query({ name: 'geolocation' as PermissionName })
			.then((result) => {
				if (cancelled) {
					return;
				}
				setGeolocationStatus(result.state as GeolocationStatus);

				// React to state changes (e.g. user toggles permission in
				// site settings without reloading the page).
				result.onchange = () => {
					if (cancelled) {
						return;
					}
					setGeolocationStatus(result.state as GeolocationStatus);
				};
			})
			.catch(() => {
				// Some browsers throw on unknown `name` — fall back silently.
			});

		return (): void => {
			cancelled = true;
		};
	}, []);

	const meOptionIsBlocked =
		geolocationStatus === 'denied' || geolocationStatus === 'unsupported';
	const meOptionSubtitle = useMemo(() => {
		if (geolocationStatus === 'denied') {
			return t(
				'Location access is blocked — enable it in your browser settings to use this option',
			);
		}
		if (geolocationStatus === 'unsupported') {
			return t('Geolocation is not available in this browser');
		}
		return undefined;
	}, [geolocationStatus, t]);

	// Parse current state from selectedValues - returns null when no filter is selected
	const currentState = useMemo(() => {
		const firstValue = selectedValues[0];
		if (firstValue == null) {
			return null; // No filter selected - show empty state
		}
		return parseCenterFromFilterOption(firstValue);
	}, [selectedValues]);

	const handleSearchCenter = useCallback(
		async (query: string): Promise<Array<SelectSearchOption>> => {
			if (query.trim().length < 2) {
				return [];
			}
			try {
				const response = await ApiService.apiGet<{
					success: boolean;
					data: Array<ProximitySearchResult>;
				}>('/clients/proximity-search', { q: query });

				if (response.success && Array.isArray(response.data)) {
					// Cache results for later coord lookups
					for (const item of response.data) {
						searchResultsRef.current.set(
							item.type === 'client'
								? `client:${item.id}`
								: `location:${item.id}`,
							item,
						);
					}

					return response.data.map((item) => {
						const value =
							item.type === 'client'
								? `client:${item.id}`
								: `location:${item.id}`;
						const badgeText =
							item.type === 'client'
								? 'Client'
								: item.type === 'city'
									? 'City'
									: item.type === 'zip'
										? 'ZIP'
										: 'County';
						const subtitle =
							item.subtitle ??
							(item.count != null
								? `${String(item.count)} clients`
								: undefined);
						return {
							value,
							label: item.label,
							subtitle:
								subtitle != null ? `${subtitle} · ${badgeText}` : badgeText,
						};
					});
				}
				return [];
			} catch (err) {
				console.error('BaseProximityFilter: Failed to search:', err);
				return [];
			}
		},
		[],
	);

	const resolveLocationCoords = useCallback(
		async (
			locationId: string,
		): Promise<{ lat: number; lng: number } | null> => {
			try {
				setIsResolvingCoords(true);
				const response = await ApiService.apiGet<{
					success: boolean;
					data?: { lat: number; lng: number; clientCount: number };
					error?: string;
				}>('/clients/proximity-center-coords', { locationId });

				if (response.success && response.data != null) {
					return { lat: response.data.lat, lng: response.data.lng };
				}
				return null;
			} catch (err) {
				console.error('BaseProximityFilter: Failed to resolve coords:', err);
				return null;
			} finally {
				setIsResolvingCoords(false);
			}
		},
		[],
	);

	const handleCenterChange = useCallback(
		async (value: string) => {
			if (value === '' || value == null) {
				// Clear filter entirely
				onValuesChange([]);
				return;
			}

			// Use existing radius or default to 25 if no filter is currently selected
			const radius = currentState?.radius ?? 25;

			if (value === ME_CENTER_VALUE) {
				onValuesChange([buildFilterOption('me', radius, ME_CENTER_LABEL)]);
				return;
			}

			const cachedResult = searchResultsRef.current.get(value);
			if (cachedResult == null) {
				return;
			}

			if (cachedResult.type === 'client') {
				// Client — coords are directly in the search result
				if (cachedResult.lat != null && cachedResult.lng != null) {
					onValuesChange([
						buildFilterOption('client', radius, cachedResult.label, {
							clientId: parseInt(cachedResult.id, 10),
							lat: cachedResult.lat,
							lng: cachedResult.lng,
						}),
					]);
				}
			} else {
				// Location — need to resolve coords
				const coords = await resolveLocationCoords(cachedResult.id);
				if (coords != null) {
					onValuesChange([
						buildFilterOption('location', radius, cachedResult.label, {
							locationId: cachedResult.id,
							lat: coords.lat,
							lng: coords.lng,
						}),
					]);
				}
			}
		},
		[currentState?.radius, onValuesChange, resolveLocationCoords],
	);

	const handleRadiusChange = useCallback(
		(value: string) => {
			if (value === '' || value == null) {
				onValuesChange([]);
				return;
			}
			const newRadius = parseInt(value, 10);
			if (isNaN(newRadius)) {
				return;
			}

			// If no filter selected, apply "me" center with chosen radius
			if (currentState == null) {
				onValuesChange([buildFilterOption('me', newRadius, ME_CENTER_LABEL)]);
				return;
			}

			if (currentState.centerType === 'me') {
				onValuesChange([buildFilterOption('me', newRadius, ME_CENTER_LABEL)]);
				return;
			}

			// Rebuild with existing center data but new radius
			const selectedValue = selectedValues[0];
			if (selectedValue == null) {
				return;
			}
			const data = selectedValue.data as ProximityCenterData;
			const centerLabel = selectedValue.label.replace(/ - \d+ miles$/, '');
			onValuesChange([
				buildFilterOption(data.centerType, newRadius, centerLabel, {
					clientId: data.clientId,
					locationId: data.locationId,
					lat: data.lat,
					lng: data.lng,
				}),
			]);
		},
		[currentState, onValuesChange, selectedValues],
	);

	const handleLoadCenterById = useCallback(
		async (id: string): Promise<SelectSearchOption | null> => {
			if (id === ME_CENTER_VALUE) {
				return {
					value: ME_CENTER_VALUE,
					label: ME_CENTER_LABEL,
					subtitle: meOptionSubtitle,
					disabled: meOptionIsBlocked,
				};
			}
			// For previously selected centers, we already have the label
			const cached = searchResultsRef.current.get(id);
			if (cached != null) {
				return { value: id, label: cached.label, subtitle: cached.subtitle };
			}
			// Fallback: extract label from current filter value
			const firstSelected = selectedValues[0];
			if (firstSelected != null) {
				const label = firstSelected.label.replace(/ - \d+ miles$/, '');
				return { value: id, label };
			}
			return null;
		},
		[selectedValues, meOptionSubtitle, meOptionIsBlocked],
	);

	const isDisabled = disabled || isModalOpen || isPreset;

	// PD-13545 — when the currently-applied filter pins "Me" but geolocation
	// is blocked/unsupported, the backend query will silently return zero
	// results. Surface a one-line warning so the user knows WHY the list is
	// empty (and what action they have to take).
	const showMeBlockedWarning =
		meOptionIsBlocked && currentState?.centerType === 'me';

	return (
		<div className="flex flex-col gap-2">
			{showMeBlockedWarning && (
				<div
					className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
					role="status"
				>
					{meOptionSubtitle}
				</div>
			)}
			<FormField
				name={`${filterType}-center`}
				label={t(config.label)}
				size={size}
			>
				<div className={cn(isDisabled && 'opacity-60')}>
					<SelectSearch
						mode="dynamic"
						value={currentState?.centerValue ?? ''}
						onChange={handleCenterChange}
						searchItems={handleSearchCenter}
						loadItemById={handleLoadCenterById}
						placeholder={t('Search client or location...')}
						disabled={isDisabled || isResolvingCoords}
						size={size}
						clearable={true}
						searchGroupLabel={t('Search Results')}
						recentGroupLabel={t('Options')}
						loadRecent={async () => [
							{
								value: ME_CENTER_VALUE,
								label: t(ME_CENTER_LABEL),
								subtitle: meOptionSubtitle,
								disabled: meOptionIsBlocked,
							},
						]}
					/>
				</div>
			</FormField>
			<FormField name={`${filterType}-radius`} label={t('Radius')} size={size}>
				<div className={cn(isDisabled && 'opacity-60')}>
					<Select
						name={`${filterType}-radius`}
						options={RADIUS_OPTIONS.map((opt) => ({
							value: opt.value,
							label: t(opt.label),
						}))}
						value={currentState != null ? String(currentState.radius) : ''}
						onChange={handleRadiusChange}
						placeholder={t('Select radius...')}
						disabled={isDisabled}
						size={size}
					/>
				</div>
			</FormField>
		</div>
	);
};
